/**
 * L2 idle effects for Greendale Kingdom (kingdom.png).
 *
 * <p>Anchor coordinates measured against the 2172×724 panorama at
 * {@code apps/mobile/src/assets/map_mg/kingdom.png}. All positions are
 * percentages of that frame so they hold steady regardless of viewport
 * height.
 *
 * <p>Effects in this layer:
 * <ul>
 *   <li>Cottage chimney smoke — three rising puffs above the chimney.
 *   <li>Cottage window glow flicker — yellow gradient pulse over the
 *       front windows.
 *   <li>Pine canopy shimmer — soft brightness pulse over the central
 *       pine cluster, "wind through canopy" feel.
 *   <li>Dragon blink — periodic eyelid drop on the dragon peeking from
 *       the rocks at the right edge.
 * </ul>
 */
export function KingdomMidEffects() {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        animation: 'gtFadeIn 300ms ease-out',
      }}
    >
      {/* Chimney smoke — 3 puffs at staggered phase, anchored to the
          chimney top (~25% x, ~34% y in panorama coords; the panorama
          has a tall transparent sky band, so painted assets sit lower
          than naive top-down measurement suggests). Each puff is a
          soft circle that rises + expands + fades. */}
      <ChimneySmokePuff delay="0s" />
      <ChimneySmokePuff delay="1.6s" />
      <ChimneySmokePuff delay="3.2s" />

      {/* Cottage upper-left dormer — warm yellow gradient breathing
          over the painted dormer window. Position re-measured from
          the panorama on 2026-05-14 by sampling the brightest yellow
          clusters: both dormers cluster around natural (472, 415)
          and (671, 415) → centres 21.75% & 30.9% x, 57.3% y on the
          2172×724 frame. Previous 40% y was a roof-thatch position,
          well above the painted glow. */}
      <div
        style={{
          position: 'absolute',
          top: '54.3%',
          left: '20.45%',
          width: '2.6%',
          height: '6%',
          borderRadius: '30% / 50%',
          background:
            'radial-gradient(ellipse at center, rgba(255,205,110,0.85), rgba(255,180,60,0.0) 75%)',
          mixBlendMode: 'screen',
          animation: 'gtFlicker 5s ease-in-out infinite',
        }}
      />
      {/* Cottage upper-right dormer — desync'd phase. */}
      <div
        style={{
          position: 'absolute',
          top: '54.3%',
          left: '29.6%',
          width: '2.6%',
          height: '6%',
          borderRadius: '30% / 50%',
          background:
            'radial-gradient(ellipse at center, rgba(255,205,110,0.85), rgba(255,180,60,0.0) 75%)',
          mixBlendMode: 'screen',
          animation: 'gtFlicker 6.3s -1.7s ease-in-out infinite',
        }}
      />

      {/* Pine canopy shimmer — gentle brightness pulse over the
          central pine forest (~36-65% x, ~50-90% y). Sells "wind in
          the trees" without needing per-tree assets. */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '36%',
          width: '30%',
          height: '40%',
          background:
            'radial-gradient(ellipse at center, rgba(200,255,200,0.18), transparent 70%)',
          mixBlendMode: 'screen',
          animation: 'gtCanopy 6s ease-in-out infinite',
        }}
      />

      {/* Dragon blink — opaque eyelid that drops over the dragon's
          eyes (~78% x, ~75% y) for ~120ms every ~5s. Tinted dark
          green to match the dragon's scales. */}
      <div
        style={{
          position: 'absolute',
          top: '75%',
          left: '76%',
          width: '5%',
          height: '1.6%',
          background:
            'linear-gradient(180deg, rgba(40,60,40,0.0), rgba(40,60,40,0.9) 50%, rgba(40,60,40,0.0))',
          animation: 'gtBlink 5.4s steps(1) infinite',
        }}
      />
    </div>
  );
}

interface ChimneySmokePuffProps {
  delay: string;
}

function ChimneySmokePuff({ delay }: ChimneySmokePuffProps) {
  return (
    <div
      style={{
        position: 'absolute',
        // Producer audit (2026-05-14) — re-measured against the
        // current kingdom.png by sampling the panorama's topmost
        // opaque pixels: the brick chimney pokes through at natural
        // pano coords (525, 252), i.e. 24.2% x / 34.8% y of the
        // 2172×724 frame. The spawn box is positioned so the puff's
        // CENTRE lands on that chimney-top pixel — half the puff
        // overlaps the cap at t=0 (the radial gradient blends it
        // into the brick) and the animation lifts the puff up out
        // of the chimney from there. Prior values (30 / 21.4) were
        // ~34px left and ~6px high, so the smoke looked like a sky
        // puff rather than a chimney emission.
        top: '30.8%',
        left: '22.8%',
        // Bigger puff so the smoke reads at a glance instead of as
        // a 21px speck. Was 1.4% × 4%; doubling registers without
        // breaking the chimney's visual scale.
        width: '2.8%',
        height: '8%',
        borderRadius: '50%',
        background:
          'radial-gradient(circle at 50% 60%, rgba(245,245,245,0.95), rgba(220,220,220,0.55) 60%, rgba(200,200,200,0.0) 100%)',
        filter: 'blur(3px)',
        animation: `gtSmoke 4.8s ease-out ${delay} infinite`,
        opacity: 0,
      }}
    />
  );
}
